import {
  CompanyAccess,
  CompanyTerminationDetails,
  RelationAccess,
} from '../../core/models/smartclock/company-access.model';
import { apiErrorOf } from './api-error.utils';

/**
 * Lectura del término de suscripción (subscription-termination.spec.md del API): fechas para
 * los avisos y detección de los errores `COMPANY_TERMINATED` / `COMPANY_READ_ONLY`.
 *
 * Las fechas se muestran `dd-mm-aaaa` (textos aprobados del prototipo) y SIEMPRE en
 * America/Santiago: `blockedFrom` y `readOnlyUntil` son instantes, y el día que importa es el
 * calendario de Chile, no el del navegador.
 */

export const COMPANY_TERMINATED = 'COMPANY_TERMINATED';
export const COMPANY_READ_ONLY = 'COMPANY_READ_ONLY';

const YMD = /^(\d{4})-(\d{2})-(\d{2})$/;

const SANTIAGO_DAY = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/Santiago',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** `YYYY-MM-DD` (fecha de negocio, sin zona) → `dd-mm-aaaa`; '' si falta o no calza. */
export function formatServiceDay(ymd: string | null | undefined): string {
  const match = typeof ymd === 'string' ? YMD.exec(ymd) : null;
  return match ? `${match[3]}-${match[2]}-${match[1]}` : '';
}

/**
 * Día calendario de Chile (America/Santiago) de un instante, `dd-mm-aaaa` (o con otro separador,
 * p. ej. `/` en las grillas de facturación); '' si no es válido.
 */
export function santiagoDayLabel(iso: string | null | undefined, separator = '-'): string {
  if (!iso) return '';
  const time = new Date(iso).getTime();
  return Number.isNaN(time) ? '' : santiagoLabelOf(time, separator);
}

/**
 * Último día de consulta: `readOnlyUntil` es las 00:00 (America/Santiago) del día en que se
 * cierra el acceso, así que el último día con consulta es el anterior (el del instante previo).
 */
export function lastReadOnlyDayLabel(readOnlyUntil: string | null | undefined): string {
  if (!readOnlyUntil) return '';
  const time = new Date(readOnlyUntil).getTime();
  return Number.isNaN(time) ? '' : santiagoLabelOf(time - 1, '-');
}

/**
 * ¿La persona quedó fuera de la empresa? `blocked` deja fuera a todos; `read_only`, sólo al
 * trabajador (los administrativos consultan). El API es la autoridad: esto sólo adelanta lo que
 * el gate ya responde con `403 COMPANY_TERMINATED`.
 */
export function isCompanyLockedOut(
  access: CompanyAccess | null | undefined,
  role: string | null | undefined,
): boolean {
  if (!access) return false;
  if (access.state === 'blocked') return true;
  return access.state === 'read_only' && role === 'worker';
}

/** Relación de la cuenta deshabilitada por el término (`access.available === false`). */
export function isRelationUnavailable(relation: { access?: RelationAccess | null }): boolean {
  return relation.access?.available === false;
}

/** `details` de un `403 COMPANY_TERMINATED` (vacío si el API no los manda); null si es otro error. */
export function companyTerminatedDetails(err: unknown): CompanyTerminationDetails | null {
  const info = apiErrorOf(err);
  if (info.type !== COMPANY_TERMINATED) return null;
  const details = info.details ?? {};
  const result: CompanyTerminationDetails = {};
  for (const key of ['companyId', 'companyName', 'lastServiceDay', 'readOnlyUntil'] as const) {
    const value = details[key];
    if (typeof value === 'string' || value === null) result[key] = value;
  }
  return result;
}

/** ¿Es un `403 COMPANY_READ_ONLY` (escritura durante el modo consulta)? */
export function isCompanyReadOnlyError(err: unknown): boolean {
  return apiErrorOf(err).type === COMPANY_READ_ONLY;
}

function santiagoLabelOf(time: number, separator: string): string {
  const parts = SANTIAGO_DAY.formatToParts(new Date(time));
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((item) => item.type === type)?.value ?? '';
  return [part('day'), part('month'), part('year')].join(separator);
}
